import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useAuthStore } from "../stores/authStore";
import { EmailVerificationDialog } from "@/features/auth/EmailVerificationDialog";
import { LoginForm } from "@/features/auth/LoginForm";
import { isResumingRedeem, REDEEM_PATH } from "@/features/seat-orders/redeemCode";
import { useEmailVerification } from "@/hooks/useEmailVerification";
import { sanitizeRedirect } from "@/hooks/usePasswordLoginForm";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { error?: string; registered?: boolean; passwordReset?: boolean; redirect?: string } => ({
    error: typeof search.error === "string" ? search.error : undefined,
    registered: search.registered === true || search.registered === "true" || undefined,
    passwordReset: search.passwordReset === true || search.passwordReset === "true" || undefined,
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
});

function SuccessBanner({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-arc-success-50 border-arc-success-200 rounded-lg border p-4"
    >
      <p className="text-arc-success-700 text-sm font-medium">{children}</p>
    </div>
  );
}

/**
 * Leaves for a sanitised in-app target with a full-page navigation, as signing in does, so a target carrying a query
 * string or hash arrives intact.
 */
function RedirectAway({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function getOAuthErrorMessage(error: string): string {
  switch (error) {
    case "auth_failed":
      return "Authentication failed. Please try again.";
    case "auth_error":
      return "An error occurred during authentication.";
    case "no_code":
      return "Invalid authentication response.";
    default:
      return "An unexpected error occurred.";
  }
}

function LoginPage() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const {
    error,
    registered: isRegistered,
    passwordReset: isPasswordReset,
    redirect,
  } = Route.useSearch();

  // One pending email-verification attempt, held only in memory. Dismissing
  // the dialog clears it; a later login attempt starts a fresh one.
  const {
    attempt,
    beginAttempt,
    clearAttempt,
    submitCode,
    resendCode,
    verificationError,
    resendConfirmed,
    isConfirming,
    isResending,
  } = useEmailVerification(redirect);

  // Sent here from a seat invite (LMS-188): offer sign-up too, since most participants have no account yet.
  const [resumingRedeem] = useState(() => isResumingRedeem(redirect));

  useEffect(() => {
    if (error) {
      console.error("Login error:", error);
    }
  }, [error]);

  // Redirect if already authenticated: to where they were going, when a page sent them here, otherwise My Learning.
  if (isAuthenticated && !isLoading) {
    return redirect ? (
      <RedirectAway to={sanitizeRedirect(redirect)} />
    ) : (
      <Navigate to="/my-learning" />
    );
  }

  return (
    <div className="bg-arc-gray-50 flex min-h-screen items-center justify-center px-4 py-16">
      <div className="flex w-full max-w-[450px] flex-col gap-4">
        {resumingRedeem && !isRegistered && (
          <div className="bg-arc-primary-50 border-arc-primary-200 rounded-lg border p-4">
            <p className="text-arc-gray-700 text-sm">
              You&apos;ve been invited to a course. Log in to confirm your enrolment, or{" "}
              <Link
                to="/register"
                search={{ redirect: REDEEM_PATH }}
                className="text-arc-primary-900 font-semibold underline"
              >
                create an account
              </Link>{" "}
              if you don&apos;t have one yet.
            </p>
          </div>
        )}

        {/* Registration success banner */}
        {isRegistered && (
          <SuccessBanner>Account created successfully! Please log in below.</SuccessBanner>
        )}

        {/* Password reset success banner */}
        {isPasswordReset && (
          <SuccessBanner>
            Password reset successful! Please log in with your new password.
          </SuccessBanner>
        )}

        {/* OAuth fallback error display (backwards compat during migration) */}
        {error && (
          <div className="bg-arc-error-50 border-arc-error-200 rounded-lg border p-4">
            <p className="text-arc-error-700 text-sm">{getOAuthErrorMessage(error)}</p>
          </div>
        )}

        <LoginForm redirect={redirect} onVerificationRequired={beginAttempt} />
      </div>

      <EmailVerificationDialog
        isOpen={attempt !== null}
        onClose={clearAttempt}
        onSubmitCode={submitCode}
        onResendCode={resendCode}
        verificationError={verificationError}
        resendConfirmed={resendConfirmed}
        isConfirming={isConfirming}
        isResending={isResending}
      />
    </div>
  );
}
